import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import {
  referralService,
  type ReferralFilters,
  type QuickReferralPartnerInput,
  type ReferralPartner,
  type ReferralPartnerType,
} from '../referralService';

const REFERRALS_KEY = 'referrals';

/** Invalidate every cached referral view (lists, search feed, summaries). */
export function invalidateReferralQueries(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: [REFERRALS_KEY] });
  // Referral names are embedded in receipt lists.
  queryClient.invalidateQueries({ queryKey: ['diagnostic'] });
}

export function useReferralPartners(
  page = 1,
  limit = 20,
  filters?: ReferralFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'list', page, limit, filters],
    queryFn: () => referralService.getReferralPartners(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
    enabled,
  });
}

/** Lightweight feed used by the OPD receipt combobox. */
export function useReferralSearch(q?: string, limit = 25, type?: ReferralPartnerType | 'ALL') {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'search', q ?? '', limit, type ?? 'ALL'],
    queryFn: () => referralService.searchReferralPartners(q, limit, type),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useReferralPartner(id: string) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'detail', id],
    queryFn: () => referralService.getReferralPartner(id),
    enabled: !!id,
  });
}

export function useReferralSummary(id: string, enabled = true) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'summary', id],
    queryFn: () => referralService.getSummary(id),
    enabled: !!id && enabled,
  });
}

export function useReferralPayouts(id: string, page = 1, limit = 20, enabled = true) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'payouts', id, page, limit],
    queryFn: () => referralService.getPayouts(id, page, limit),
    placeholderData: (previous) => previous,
    enabled: !!id && enabled,
  });
}

export function useTrashedReferralPartners(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'trash', page, limit, search],
    queryFn: () => referralService.getTrashed(page, limit, search),
    placeholderData: (previous) => previous,
  });
}

export function useNextReferralCode(enabled = false) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'next-code'],
    queryFn: () => referralService.getNextCode(),
    enabled,
  });
}

export function useCreateReferralPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ReferralPartner>) => referralService.createReferralPartner(data),
    onSuccess: () => invalidateReferralQueries(queryClient),
  });
}

/**
 * Inline quick-create from the OPD receipt. Idempotent server-side (reuses an
 * existing partner for the same employee/mobile) and returns the partner so the
 * caller can select it immediately.
 */
export function useQuickCreateReferralPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: QuickReferralPartnerInput) => referralService.quickCreate(data),
    onSuccess: () => invalidateReferralQueries(queryClient),
  });
}

export function useUpdateReferralPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ReferralPartner> }) =>
      referralService.updateReferralPartner(id, data),
    onSuccess: () => invalidateReferralQueries(queryClient),
  });
}

export function useDeleteReferralPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => referralService.deleteReferralPartner(id),
    onSuccess: () => invalidateReferralQueries(queryClient),
  });
}

/** Bulk soft-delete — moves the selected partners to the trash. */
export function useBulkTrashReferralPartners() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => referralService.bulkTrash(ids),
    onSuccess: () => invalidateReferralQueries(queryClient),
  });
}

export function useRestoreReferralPartners() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => referralService.restore(ids),
    onSuccess: () => invalidateReferralQueries(queryClient),
  });
}

export function usePermanentlyDeleteReferralPartners() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => referralService.permanentDelete(ids),
    onSuccess: () => invalidateReferralQueries(queryClient),
  });
}

// ── Payouts ──

/** Accrued referral shares grouped by partner (outstanding / settled). */
export function useReferralPayoutSummary(params?: {
  status?: string;
  referralId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'payout-summary', params],
    queryFn: () => referralService.getPayoutSummary(params),
    placeholderData: (previous) => previous,
  });
}

/** Referral payout vouchers. */
export function useReferralPayoutVouchers(params?: {
  referralId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'payout-vouchers', params],
    queryFn: () => referralService.getPayoutVouchers(params),
    placeholderData: (previous) => previous,
  });
}

export function useReferralPayout(id: string, enabled = true) {
  return useQuery({
    queryKey: [REFERRALS_KEY, 'payout-voucher', id],
    queryFn: () => referralService.getPayout(id),
    enabled: !!id && enabled,
  });
}

export function useSettleReferralPayouts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      referralId: string;
      dateTo?: string;
      method?: string;
      notes?: string;
    }) => referralService.settlePayouts(data),
    onSuccess: () => {
      // Refresh the payout views, the receipt-side caches (settlements) and the
      // unified voucher ledger / Top Sheet, which both include referral payouts.
      invalidateReferralQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['diagnostic'] });
      queryClient.invalidateQueries({ queryKey: ['doctor-payments'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
    },
  });
}
